import { useEffect, useRef, useState } from 'react';
import { BellRing, Moon } from 'lucide-react';
import { EMAIL_MODE_LABEL, type EmailMode, type EmailPrefs, type Me, type NotifyPrefs } from '@kaba/shared';
import { api } from '../lib/api';
import { usePublicConfig } from '../lib/auth';
import { queryClient } from '../lib/board';
import { disablePush, enablePush, pushState, type PushState } from '../lib/push';
import { toast } from '../lib/toast';
import { Button, cx } from './ui/ui';

const TYPES: [keyof EmailPrefs, string][] = [
  ['mentioned', 'Jemand erwähnt mich'],
  ['assigned', 'Mir wird eine Karte zugewiesen'],
  ['due', 'Frist läuft in 24 Stunden ab'],
  ['comment', 'Kommentar auf beobachteter Karte'],
  ['invited', 'Ich werde zu einem Board hinzugefügt'],
  ['automation', 'Hinweise aus Automationen'],
  ['reminder', 'Erinnerungen aus Regeln (Frist, Start, Termin, Eskalation)'],
  ['handover', 'Übergaben: Aufgabe kann beginnen'],
  ['form', 'Neue Einsendungen über Formulare'],
];

const timeInput = 'min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-base sm:min-h-9 sm:text-sm';

export function NotificationSettings({ me }: { me: Me }) {
  const { data: cfg } = usePublicConfig();
  // Lokal sofort anzeigen (der Query-Cache meldet Änderungen erst einen Tick später)
  const [prefs, setPrefs] = useState(me.notifyPrefs);
  const latestRef = useRef(prefs);
  useEffect(() => {
    latestRef.current = me.notifyPrefs;
    setPrefs(me.notifyPrefs);
  }, [me.notifyPrefs]);

  /** Änderung immer auf den neuesten Stand anwenden (schnelle Klicks hintereinander) */
  const save = async (change: NotifyPrefs | ((p: NotifyPrefs) => NotifyPrefs)) => {
    // Laufenden Abruf abbrechen, sonst überschreibt seine (ältere) Antwort die Änderung
    void queryClient.cancelQueries({ queryKey: ['me'] });
    const next = typeof change === 'function' ? change(latestRef.current) : change;
    const withTz = { ...next, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null };
    latestRef.current = withTz;
    setPrefs(withTz);
    const latest = queryClient.getQueryData<Me>(['me']) ?? me;
    queryClient.setQueryData<Me>(['me'], { ...latest, notifyPrefs: withTz, emailPrefs: withTz.email });
    try {
      await api.put('/api/me/notify-prefs', withTz);
    } catch (err) {
      toast.error((err as Error).message);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    }
  };
  const setType = (channel: 'email' | 'push', k: keyof EmailPrefs, v: boolean) => void save((p) => ({ ...p, [channel]: { ...p[channel], [k]: v } }));

  return (
    <section className="flex flex-col gap-4" aria-labelledby="notify-h">
      <h2 id="notify-h" className="text-lg font-semibold">
        Benachrichtigungen
      </h2>
      <p className="-mt-2 text-sm text-[var(--ink-soft)]">
        Im Posteingang der App erscheint immer alles. E-Mails gehen nur raus, wenn du die Benachrichtigung in der App noch nicht gelesen hast.
      </p>

      <PushDevice />

      <table className="w-full overflow-hidden rounded-xl border border-[var(--line)] text-sm">
        <thead className="bg-black/[0.03] text-xs text-[var(--ink-soft)] dark:bg-white/5">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Wann</th>
            <th className="w-20 px-2 py-2 font-medium">E-Mail</th>
            <th className="w-20 px-2 py-2 font-medium">Push</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--line)]">
          {TYPES.map(([k, label]) => (
            <tr key={k}>
              <td className="px-3 py-2">{label}</td>
              {(['email', 'push'] as const).map((ch) => (
                <td key={ch} className="text-center">
                  <label className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center">
                    <input
                      type="checkbox"
                      aria-label={`${label}: ${ch === 'email' ? 'E-Mail' : 'Push'}`}
                      checked={prefs[ch][k]}
                      onChange={(e) => setType(ch, k, e.target.checked)}
                      className="size-5 accent-[var(--color-accent)]"
                    />
                  </label>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium">E-Mails</legend>
        {!cfg?.mailEnabled && (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">
            Auf dieser Instanz ist noch kein E-Mail-Versand eingerichtet. Deine Auswahl gilt, sobald ein Admin SMTP einträgt.
          </p>
        )}
        {(Object.keys(EMAIL_MODE_LABEL) as EmailMode[]).map((m) => (
          <label key={m} className={cx('flex min-h-11 items-center gap-3 rounded-xl border px-3', prefs.emailMode === m ? 'border-accent bg-accent-soft/50' : 'border-[var(--line)]')}>
            <input type="radio" name="emailMode" checked={prefs.emailMode === m} onChange={() => void save((p) => ({ ...p, emailMode: m }))} className="size-5 accent-[var(--color-accent)]" />
            <span className="text-sm">{EMAIL_MODE_LABEL[m]}</span>
          </label>
        ))}
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 flex items-center gap-1.5 text-sm font-medium">
          <Moon size={15} /> Ruhezeiten (keine Push-Nachrichten, E-Mails warten)
        </legend>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={prefs.quiet.enabled}
              onChange={(e) => void save((p) => ({ ...p, quiet: { ...p.quiet, enabled: e.target.checked } }))}
              className="size-5 accent-[var(--color-accent)]"
            />
            Täglich von
          </label>
          <input
            type="time"
            aria-label="Ruhezeit ab"
            className={timeInput}
            value={prefs.quiet.from}
            disabled={!prefs.quiet.enabled}
            onChange={(e) => e.target.value && void save((p) => ({ ...p, quiet: { ...p.quiet, from: e.target.value } }))}
          />
          <span className="text-sm">bis</span>
          <input
            type="time"
            aria-label="Ruhezeit bis"
            className={timeInput}
            value={prefs.quiet.to}
            disabled={!prefs.quiet.enabled}
            onChange={(e) => e.target.value && void save((p) => ({ ...p, quiet: { ...p.quiet, to: e.target.value } }))}
          />
        </div>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={prefs.quiet.weekend}
            onChange={(e) => void save((p) => ({ ...p, quiet: { ...p.quiet, weekend: e.target.checked } }))}
            className="size-5 accent-[var(--color-accent)]"
          />
          Am Wochenende ganz ruhig
        </label>
        <p className="text-xs text-[var(--ink-soft)]">Zeitzone: {prefs.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
      </fieldset>
    </section>
  );
}

function PushDevice() {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void pushState().then(setState);
  }, []);
  const run = async (fn: () => Promise<PushState>) => {
    setBusy(true);
    try {
      setState(await fn());
    } catch (err) {
      toast.error(`Push nicht möglich: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  // Platz von Anfang an reservieren, damit nichts verrutscht, während der Status lädt
  if (!state)
    return (
      <div className="flex min-h-[3.75rem] items-center gap-3 rounded-xl border border-[var(--line)] p-3 text-sm text-[var(--ink-soft)]">
        <BellRing size={18} className="shrink-0" /> Push-Status wird geprüft …
      </div>
    );
  const text: Record<PushState, string> = {
    unsupported: 'Dieser Browser unterstützt keine Push-Benachrichtigungen.',
    'needs-install': 'Auf iPhone und iPad gibt es Push nur in der installierten App: Teilen → „Zum Home-Bildschirm“, dann KaBa von dort öffnen.',
    denied: 'Benachrichtigungen sind für KaBa im Browser blockiert. Erlaube sie in den Website-Einstellungen und lade die Seite neu.',
    off: 'Auf diesem Gerät ist Push aus.',
    on: 'Push ist auf diesem Gerät aktiv.',
  };
  return (
    <div className={cx('flex min-h-[3.75rem] flex-wrap items-center gap-3 rounded-xl border p-3', state === 'on' ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40' : 'border-[var(--line)]')}>
      <BellRing size={18} className="shrink-0 text-[var(--ink-soft)]" />
      <p className="min-w-0 flex-1 text-sm" role="status">
        {text[state]}
      </p>
      {state === 'off' && (
        <Button variant="primary" disabled={busy} onClick={() => void run(enablePush)}>
          Auf diesem Gerät aktivieren
        </Button>
      )}
      {state === 'on' && (
        <>
          <Button
            disabled={busy}
            onClick={async () => {
              const r = await api.post<{ delivered: number }>('/api/push/test', {});
              toast.info(r.delivered ? `Test an ${r.delivered} Gerät(e) gesendet.` : 'Kein Gerät erreicht.');
            }}
          >
            Test senden
          </Button>
          <Button disabled={busy} onClick={() => void run(disablePush)}>
            Ausschalten
          </Button>
        </>
      )}
    </div>
  );
}
